import { TableFilterConfig, TableFilterOption } from '@/components/data-table/filters-v2';
import i18n from '@/core/i18n';
import {
  DEFAULT_PAGE_SIZE_CLIENTS_SHORT_ENTITIES,
  DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES,
} from '@/lib/constants/pagination';
import { getShortClients } from '@/modules/clients/api/clients.api';
import { getInvoiceStatusOptions } from '@/modules/common';
import { EntityAvatar } from '@/modules/common/components/molecules/avatar/entity-avatar';
import { getShortProjects } from '@/modules/projects';
import { renderProjectFilterLabelWithAvatar } from '@/modules/projects/helpers/project-dropdown-option';

import { InvoiceStatus } from '../types';
import {
  INVOICES_FILTER_KEY_CLIENT,
  INVOICES_FILTER_KEY_PROJECT,
  INVOICES_FILTER_KEY_SEARCH,
  INVOICES_FILTER_KEY_STATUS,
} from './invoices-filter-keys';

const SEARCH_FILTER_MIN_CHARACTERS = 2;

// NOTE: the billing-period date range is rendered as a standalone DateRangePickerV2
// next to the filters (see invoices-page.tsx), not as a dropdown filter.
export const getInvoicesFilterConfig = (): TableFilterConfig[] => [
  {
    type: 'search',
    name: INVOICES_FILTER_KEY_SEARCH,
    label: i18n.t('common:search'),
    minCharacters: SEARCH_FILTER_MIN_CHARACTERS,
    placeholder: i18n.t('authenticated.invoices:searchPlaceholder'),
  },
  {
    type: 'autocomplete',
    name: INVOICES_FILTER_KEY_STATUS,
    label: i18n.t('authenticated.invoices:filters.status'),
    displayDot: true,
    options: getInvoiceStatusOptions([
      InvoiceStatus.Open,
      InvoiceStatus.InProgress,
      InvoiceStatus.Overdue,
      InvoiceStatus.Paid,
      InvoiceStatus.Error,
    ]),
  },
  {
    type: 'dynamic-autocomplete',
    name: INVOICES_FILTER_KEY_PROJECT,
    label: i18n.t('authenticated.invoices:filters.project'),
    withSearch: true,
    loadOptions: async params =>
      getShortProjects({
        filter: { searchString: params.searchString },
        pagination: { cursor: params.cursor, pageSize: DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES },
      }),
    renderLabel: renderProjectFilterLabelWithAvatar,
  },
  {
    type: 'dynamic-autocomplete',
    name: INVOICES_FILTER_KEY_CLIENT,
    label: i18n.t('authenticated.invoices:filters.client'),
    withSearch: true,
    loadOptions: async params =>
      getShortClients({
        filter: { searchString: params.searchString },
        pagination: { cursor: params.cursor, pageSize: DEFAULT_PAGE_SIZE_CLIENTS_SHORT_ENTITIES },
      }),
    renderLabel: (value: string, option: TableFilterOption) => (
      <div className="flex items-center gap-2">
        <EntityAvatar name={value} image={option.avatar} size="xs" shape="rounded" />
        <span className="text-sm">{value}</span>
      </div>
    ),
  },
];
